/** @jsxRuntime classic */
/** @jsx jsx */

import { Builder } from '@builder.io/react';
import { css, jsx } from '@emotion/react';
import { readCookie } from '@zapier/cookies';
import {
  Alert,
  Button,
  Colors,
  Icon,
  Shadows,
  Typography,
} from '@zapier/design-system';
import { labelStyles } from '@zapier/style-encapsulation';
import { useRouter } from 'next/router';
import { ReactNode, SyntheticEvent, useEffect, useState } from 'react';
import { useDeepCompareEffect } from '../../hooks/useDeepCompareEffect';
import { COLOR_KEYS } from '../../constants';
import {
  EventAction,
  InteractionName,
  useAvroTracking,
} from '../../observability';
import { FormBlock, FormField, FormValues } from '../../rest';
import {
  getFieldTypesMap,
  transformStringifiedValue,
} from '../HubSpotFormV2/HubSpotFormV2';
import { RichTextHtml } from '../RichTextHtml/RichTextHtml';
import { Field } from './Field';

type FieldName = string;
type CookieName = string;

export interface FormProps {
  backgroundColor?: keyof typeof COLOR_KEYS;
  blocks?: FormBlock[];
  /**
   * Map that indicates the name of the field that
   * the cookie should be bound to and the name of the
   * cookie from which we get the value.
   * */
  cookieBindings?: Record<FieldName, CookieName>;
  confirmationMessage?: ReactNode;
  defaultValues?: FormValues;
  description?: ReactNode;
  disableSpacing?: boolean;
  error?: string;
  fieldErrors?: Record<string, string>;
  isDisabled?: boolean;
  onSubmit?: (values: FormValues) => void;
  submitButtonColor?: 'primary' | 'primary-alt';
  submitButtonText?: string;
  title?: string;
}

const Styles = labelStyles('Form', {
  root: (
    backgroundColor: FormProps['backgroundColor'],
    disableSpacing?: boolean,
  ) =>
    css({
      maxWidth: 640,
      boxSizing: 'border-box',
      borderRadius: 10,
      backgroundColor: backgroundColor
        ? Colors[backgroundColor]
        : Colors.neutral100,
      ...(disableSpacing
        ? { margin: '0 auto' }
        : { margin: '12px auto', padding: '32px 24px' }),
      ...(backgroundColor !== 'inherit' && {
        border: `1px solid ${Colors.gray20}`,
        boxShadow: Shadows.elevation5,
      }),
    }),

  header: css({
    display: 'grid',
    placeItems: 'center',
    gap: '8px',
    marginBottom: '32px',
  }),

  title: css({
    ...Typography.pageHeader9,
    color: Colors.neutral800,
    textAlign: 'center',
  }),

  description: css({
    ...Typography.smallPrint1,
    maxWidth: '384px',
    color: Colors.neutral700,
    textAlign: 'center',
  }),

  form: css({
    display: 'grid',
    gap: 20,
  }),

  block: css({
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: 20,
  }),

  button: css({
    width: '100%',
    marginTop: '12px',
  }),

  confirmation: css({
    minHeight: '480px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '20px',
    padding: '20px 20px 40px',
    boxSizing: 'border-box',
    textAlign: 'center',
    ...Typography.subHeader3Medium,
  }),

  hidden: css({
    display: 'none',
  }),
});

export function Form(props: FormProps) {
  const router = useRouter();
  const [values, setValues] = useState<FormValues>(props.defaultValues ?? {});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const { emitUserInteractionEvent } = useAvroTracking();

  /**
   * defaultValues may change while editing/previewing or if they are bound to
   * dynamic data. If editing/previewing, we want to blindly update value.
   * Otherwise, we only want to update the value if a value hasn't already been
   * set.
   * */
  useDeepCompareEffect(() => {
    if (props.defaultValues) {
      setValues((previousValues) => {
        const defaultValues = props.defaultValues ?? {};

        if (Builder.isEditing || Builder.isPreviewing) {
          return { ...previousValues, ...defaultValues };
        }

        return Object.entries(defaultValues).reduce(
          (updatedValues, [key, value]) => {
            return previousValues[key]
              ? updatedValues
              : { ...updatedValues, [key]: value };
          },
          previousValues,
        );
      });
    }
  }, [props.defaultValues]);

  // Prefills the fields from the query params
  useEffect(() => {
    const isSlugOnly = Object.entries(router.query).length === 1;
    if (isSlugOnly) return;

    const fieldTypesMap = getFieldTypesMap(props.blocks || []);
    setValues((prevState) => {
      return Object.entries(router.query).reduce((acc, param) => {
        const type = fieldTypesMap[param[0]];

        if (!type) {
          return acc;
        }

        return {
          ...acc,
          [param[0]]: transformStringifiedValue(type, param[1]),
        };
      }, prevState);
    });
  }, [router.query, props.blocks]);

  // Prefill fields from specified cookies
  useDeepCompareEffect(() => {
    if (!props.cookieBindings) return;
    const fieldTypesMap = getFieldTypesMap(props.blocks || []);
    setValues((prevState) => {
      return Object.entries(props.cookieBindings || {}).reduce(
        (acc, [key, value]) => {
          const type = fieldTypesMap[key];
          if (!type || !value) return acc;
          return {
            ...acc,
            [key]: transformStringifiedValue(type, readCookie(value)),
          };
        },
        prevState,
      );
    });
  }, [props.blocks, props.cookieBindings]);

  useEffect(() => {
    if (props.fieldErrors) {
      setFieldErrors(props.fieldErrors);
    }
  }, [props.fieldErrors]);

  const handleFieldChange = (name: string, value: any) => {
    setValues((previousValues) => {
      return {
        ...previousValues,
        [name]: value,
      };
    });

    if (fieldErrors[name]) {
      setFieldErrors((previousFieldErrors) => {
        delete previousFieldErrors[name];
        return previousFieldErrors;
      });
    }
  };

  const isValid = (field: FormField) => {
    if (!field.isRequired) {
      return true;
    }

    const value = values[field.name];

    if (Array.isArray(value)) {
      return value.length > 0;
    }

    if (typeof value === 'string') {
      return value.trim() !== '';
    }

    return !!value;
  };

  const handleSubmit = (event: SyntheticEvent) => {
    event.preventDefault();

    if (props.isDisabled) {
      return;
    }

    const errors = props.blocks
      ?.flatMap((block) => block.fields || [])
      .reduce((errors, field) => {
        if (isValid(field)) {
          return errors;
        }

        return {
          ...errors,
          [field.name]: 'Please complete this required field',
        };
      }, {});

    if (errors && Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    if (props.onSubmit) {
      props.onSubmit(values);
    }
  };

  return (
    <div css={Styles.root(props.backgroundColor, props.disableSpacing)}>
      {(props.title || props.description) && (
        <div css={Styles.header}>
          {props.title && <h3 css={Styles.title}>{props.title}</h3>}
          {props.description && (
            <div css={Styles.description}>{props.description}</div>
          )}
        </div>
      )}
      {props.confirmationMessage ? (
        <div css={Styles.confirmation}>
          <Icon
            name="formCheckCircle"
            color={COLOR_KEYS.success300}
            size={64}
          />
          {props.confirmationMessage}
        </div>
      ) : (
        <form css={Styles.form} onSubmit={handleSubmit} noValidate={true}>
          {props.blocks?.map((block, index) => (
            <div
              key={index}
              css={[
                Styles.block,
                !block.richText &&
                block.fields?.every(
                  (field) =>
                    field.isHidden ||
                    (field.isHiddenIfPrefilled &&
                      props.defaultValues?.[field.name]),
                )
                  ? Styles.hidden
                  : null,
              ]}
            >
              {block.fields &&
                block.fields.map((field) => (
                  <div
                    key={field.name}
                    css={
                      field.isHidden ||
                      (field.isHiddenIfPrefilled &&
                        props.defaultValues?.[field.name])
                        ? Styles.hidden
                        : null
                    }
                  >
                    <Field
                      {...field}
                      value={values[field.name]}
                      error={fieldErrors[field.name]}
                      isDisabled={props.isDisabled}
                      onChange={(value, isPristine) => {
                        if (isPristine) {
                          emitUserInteractionEvent({
                            event_action: EventAction.edit,
                            interaction_name: field.name as InteractionName,
                            interaction_raw_text: field.label,
                            option_selected: field.type,
                            custom_tracking_fields: {
                              form_title: props.title,
                            },
                          });
                        }
                        handleFieldChange(field.name, value);
                      }}
                    />
                  </div>
                ))}
              {block.richText && (
                <RichTextHtml
                  html={block.richText}
                  backgroundColor={COLOR_KEYS.neutral100}
                />
              )}
            </div>
          ))}
          {props.error && <Alert type="error">{props.error}</Alert>}
          <div css={Styles.button}>
            <Button
              color={props.submitButtonColor || 'primary'}
              disabled={props.isDisabled}
              isFullWidth={true}
              type="submit"
            >
              {props.submitButtonText ?? 'Submit'}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
